/* eslint-disable react-refresh/only-export-components */
import {
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueProps,
  MenuListProps,
  ControlProps,
  IndicatorSeparatorProps,
  ValueContainerProps,
  SingleValueProps,
  PlaceholderProps,
  InputProps,
  SelectComponentsConfig,
} from "react-select";
import { Control, OptionType } from "./select-input";
import { useCurrentRef } from "@/hooks/use-current-ref";
import React from "react";
import { combineRefs } from "@/hooks/combine-refs";
import { Button, ButtonProps } from "../custom/button";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { useTooltipProps } from "../custom/tooltip";

export const MenuListBlur = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>,
) => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const menuIsOpenRef = useCurrentRef(props.selectProps.menuIsOpen);
  const onMenuCloseRef = useCurrentRef(props.selectProps.onMenuClose);
  const controlRef = (props.selectProps as any).controlRef as
    | React.MutableRefObject<HTMLDivElement | null>
    | undefined;
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        (!controlRef?.current || !controlRef.current.contains(target))
      ) {
        if (menuIsOpenRef.current) {
          onMenuCloseRef.current();
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);
  return (
    <components.MenuList
      {...props}
      innerRef={combineRefs(props.innerRef, ref)}
    />
  );
};

export function NoIndicatorSeparator<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: IndicatorSeparatorProps<Option, IsMulti, Group>) {
  // return <components.IndicatorSeparator {...props} />;
  return (props as any).children;
}

export function NoValueContainer<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: ValueContainerProps<Option, IsMulti, Group>) {
  // return <components.ValueContainer {...props} />;
  return props.children;
}

export function NoSingleValue<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: SingleValueProps<Option, IsMulti, Group>) {
  // return <components.Value {...props} />;
  return props.children;
}

export function NoMultiValue<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: MultiValueProps<Option, IsMulti, Group>) {
  // return <components.Value {...props} />;
  return props.children;
}
export function NoPlaceholder<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: PlaceholderProps<Option, IsMulti, Group>) {
  // return <components.Value {...props} />;
  return props.children;
}
export function NoInput<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: InputProps<Option, IsMulti, Group>) {
  // return <components.Value {...props} />;
  return props.children;
}
export type DropdownOnlyPropsCommon = {
  onlyDropdownIndicator?: boolean;
  dropdownProps?: ButtonProps;
};

export type DropdownOnlyProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = DropdownOnlyPropsCommon & {
  Control: typeof components.Control<Option, IsMulti, Group>;
};

// export const iconVariants = cva("", {
//   variants: {
//     size: {
//       default: "s-5",
//       "2xs": "s-2",
//       xs: "s-3",
//       sm: "s-4",
//       lg: "s-6",
//       "2xl": "s-7",
//       icon: "s-5",
//       page: "s-4",
//     },
//   },
// });

export const DropdownIndicatorOnly = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => {
  const tooltipProps = useTooltipProps();
  return (
    <components.DropdownIndicator {...props}>
      <Button
        {...tooltipProps}
        variant="outline"
        size="icon"
        type="button"
        {...(props?.innerProps as any)}
        {...(props.selectProps as DropdownOnlyPropsCommon).dropdownProps}
      >
        <BiDotsHorizontalRounded
        // className={iconVariants({
        //   size: (props?.selectProps as DropdownOnlyPropsCommon)?.dropdownProps
        //     ?.size,
        // })}
        />
      </Button>
    </components.DropdownIndicator>
  );
};

const wrapControl =
  <
    Value,
    Option extends OptionType<Value>,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
  >(
    Control: typeof components.Control<Option, IsMulti, Group>,
  ) =>
  (props: ControlProps<Option, IsMulti, Group>) => (
    <Control
      {...props}
      innerRef={combineRefs(
        props.innerRef,
        (props.selectProps as any).controlRef,
      )}
    />
  );

export const useDropDownOnly = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  props: DropdownOnlyProps<Value, Option, IsMulti, Group>,
) => {
  const controlRef = React.useRef<HTMLDivElement | null>(null);
  const control = React.useMemo(
    () => wrapControl(props.Control ?? (Control as any)),
    [props.Control],
  );
  return {
    controlRef,
    components: {
      Control: control,
      ...(props.onlyDropdownIndicator
        ? {
            ValueContainer: NoValueContainer,
            SingleValue: NoSingleValue,
            MultiValue: NoMultiValue,
            IndicatorSeparator: NoIndicatorSeparator,
            Placeholder: NoPlaceholder,
            Input: NoInput,
            MenuList: MenuListBlur,
            DropdownIndicator: DropdownIndicatorOnly,
          }
        : {}),
    } satisfies SelectComponentsConfig<Option, IsMulti, Group>,
    controlShouldRenderValue: props.onlyDropdownIndicator ? false : undefined,
  };
};
